/**
 * Reference data barrel.
 *
 * Large option lists live in sibling JSON files so they don't clutter the
 * component code. Import the typed constants from here rather than reaching
 * into the JSON directly — that keeps a single seam we can later swap for an
 * API-backed source without touching consumers.
 *
 * ## Place lists
 *
 * Only two lists are hand-maintained — {@link US_STATES} and
 * {@link US_TERRITORIES}. Everything else is DERIVED from them, so a place is
 * added in exactly one spot and every dropdown stays consistent:
 *
 *   US_STATES (50) ─┐
 *   'District of Columbia' ─┼─► US_STATES_TERRITORIES (sorted)
 *   US_TERRITORIES (5) ─┘            ├─► US_BAR_JURISDICTIONS (+ sentinels)
 *                                    └─► ADDRESS_STATE_TERRITORIES (+ N/A)
 *
 * The U.S. law school list is a shared resource (used across apps) and lives
 * in `shared-resources`; it is re-exported here so oasis consumers keep a
 * single import site.
 *
 * ## Country lists
 *
 * {@link COUNTRIES} is the sovereign-nation list and is the default for any
 * "what country" field. {@link CITIZENSHIP_COUNTRIES} derives from it for the one
 * field that also needs American Samoa. Do not fold that entry back into
 * `countries.json` — see the note on that constant.
 */

import { US_LAW_SCHOOLS, US_LAW_SCHOOL_NOT_LISTED } from 'shared-resources';

import countries from './countries.json';
import usStates from './us-states.json';
import usTerritories from './us-territories.json';

/** Standard country list (alphabetical). */
export const COUNTRIES: readonly string[] = countries;

/**
 * American Samoa. Also an entry in {@link US_TERRITORIES}; named here because it
 * is the only territory that belongs in a country-of-CITIZENSHIP list, and that
 * list is not built from the place lists.
 *
 * People born in the other four territories are U.S. citizens by birth, so they
 * answer "yes" to the citizenship question and never reach that field. American
 * Samoans are U.S. *nationals* rather than citizens — they answer "no", and
 * "permanent allegiance to the United States (for example, as a native of
 * American Samoa and Swains Island)" is one of the five statutory criteria the
 * Citizenship screen offers. Swains Island is part of American Samoa, so this
 * single entry covers the whole criterion.
 */
export const AMERICAN_SAMOA = 'American Samoa';

/**
 * Countries for the NON-CITIZEN "country of citizenship" question: the standard
 * list plus {@link AMERICAN_SAMOA}, interleaved alphabetically rather than
 * appended, so type-ahead finds it.
 *
 * Scoped deliberately. Every other country field in the app wants sovereign
 * nations only — including the dual-citizenship field on this same screen, which
 * asks for the country *other than* the United States, and the education cards,
 * which ask what country a school is in. A U.S. territory is not a valid answer
 * to any of those, so this stays a derived view instead of a change to
 * {@link COUNTRIES}.
 */
export const CITIZENSHIP_COUNTRIES: readonly string[] = [...countries, AMERICAN_SAMOA].sort((a, b) => a.localeCompare(b));

/** The 50 U.S. states. Use for a field that is genuinely states-only. */
export const US_STATES: readonly string[] = usStates;

/** U.S. territories (excludes the District of Columbia, which is a federal district). */
export const US_TERRITORIES: readonly string[] = usTerritories;

/** The District of Columbia — neither a state nor a territory, but a valid location and bar jurisdiction. */
export const DISTRICT_OF_COLUMBIA = 'District of Columbia';

/**
 * States, D.C., and territories in ONE alphabetical run — territories are
 * interleaved rather than grouped at the bottom, so applicants can scan or
 * type-ahead to any entry.
 */
export const US_STATES_TERRITORIES: readonly string[] = [...usStates, DISTRICT_OF_COLUMBIA, ...usTerritories].sort((a, b) => a.localeCompare(b));

/** Sentinel answers for the bar-jurisdiction question; kept last, after the real places. */
export const BAR_JURISDICTION_SENTINELS: readonly string[] = ['Unknown', 'None'];

/**
 * Sentinel for a mailing address outside the U.S., where no state or territory
 * applies. Selecting it is what reveals the free-text region field, so a
 * non-U.S. address can still record its province/region and stay deliverable.
 */
export const STATE_TERRITORY_NOT_APPLICABLE = 'N/A';

/**
 * State/territory options for a MAILING ADDRESS: every U.S. place plus the `N/A`
 * escape. Distinct from {@link US_STATES_TERRITORIES}, which is for fields that
 * must be a real U.S. place (e.g. a U.S. law school's location).
 */
export const ADDRESS_STATE_TERRITORIES: readonly string[] = [...US_STATES_TERRITORIES, STATE_TERRITORY_NOT_APPLICABLE];

/**
 * U.S. bar-exam jurisdictions for the Non-J.D. "sit for the bar" question:
 * every state, D.C., and territory (alphabetical) followed by the `Unknown` and
 * `None` sentinels the AC requires.
 *
 * NOTE: this is derived from {@link US_STATES_TERRITORIES}, which also fixed a
 * gap in the previous hand-maintained list — the District of Columbia was
 * missing, despite the D.C. Bar being one of the most relevant jurisdictions for
 * DOJ applicants.
 */
export const US_BAR_JURISDICTIONS: readonly string[] = [...US_STATES_TERRITORIES, ...BAR_JURISDICTION_SENTINELS];

/**
 * Official 2026 list of U.S. law schools + the "not listed" sentinel,
 * re-exported from the shared library. The sentinel is NOT part of the array;
 * it is prepended at render time.
 */
export { US_LAW_SCHOOLS, US_LAW_SCHOOL_NOT_LISTED };
